import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ProfileTestID } from "@/constants/TestID";
import { useAnalytics } from "@/services/Analytics";
import { useGetGendersQuery, useGetPronounsQuery } from "@/store/api/userEnums";
import {
  ChosenNameField,
  FirstNameField,
  GenderField,
  LastNameField,
  PronounsField,
} from "@/ui/components/profile/edit/BasicInfoFields";
import { Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { enumsToMultiSelectOptions } from "@/utils/api/enums.util";
import type { PermissionChecker } from "@/utils/profile/permissions.util";

const baseTestID = ProfileTestID.BasicInfo;

interface BasicInfoSectionProps extends UserBasicInfo {
  onChange: (basicInfo: UserBasicInfo) => void;
  userPermissions: PermissionChecker;
}

const BasicInfoSection = ({
  chosenName: initialChosenName,
  firstName: initialFirstName,
  gender: initialGender,
  lastName: initialLastName,
  onChange,
  pronouns: initialPronouns,
  userPermissions,
}: BasicInfoSectionProps) => {
  const { track } = useAnalytics();
  const trackFieldPressIn = useCallback(
    (field: string) => {
      const fieldName = field.replace("Field", "");
      track(ProfileAnalyticsEvent.EditProfileFieldPressed(fieldName), {
        [AnalyticsFieldName.Component]: BasicInfoSection.name,
        [AnalyticsFieldName.Field]: fieldName,
        [AnalyticsFieldName.Source]: ModalPath.EditProfile,
      });
    },
    [track],
  );

  const [firstName, setFirstName] = useState<string>(initialFirstName);
  const [chosenName, setChosenName] = useState<string | undefined>(
    initialChosenName,
  );
  const [lastName, setLastName] = useState<string>(initialLastName);
  const [gender, setGender] = useState<string>(initialGender);
  const [pronouns, setPronouns] = useState<string[] | undefined>(
    initialPronouns,
  );
  useEffect(() => {
    const basicInfo = { chosenName, firstName, gender, lastName, pronouns };
    onChange(basicInfo);
  }, [firstName, chosenName, lastName, gender, pronouns, onChange]);

  const { data: gendersData } = useGetGendersQuery();
  const genderOptions = useMemo(
    () =>
      enumsToMultiSelectOptions({
        enumOptions: gendersData,
        valueField: "code",
      }),
    [gendersData],
  );

  const { data: pronounsData } = useGetPronounsQuery();
  const pronounOptions = useMemo(
    () =>
      enumsToMultiSelectOptions({
        enumOptions: pronounsData,
        valueField: "label",
      }),
    [pronounsData],
  );

  return (
    <VStack gap={12}>
      <Text testID={baseTestID} variant="h5">
        Basic info
      </Text>
      <HStack gap={24}>
        <Box flex={1}>
          <FirstNameField
            firstName={firstName}
            setFirstName={setFirstName}
            trackFieldPressIn={trackFieldPressIn}
            userPermissions={userPermissions}
          />
        </Box>
        <Box flex={1}>
          <LastNameField
            lastName={lastName}
            setLastName={setLastName}
            trackFieldPressIn={trackFieldPressIn}
            userPermissions={userPermissions}
          />
        </Box>
      </HStack>
      <ChosenNameField
        chosenName={chosenName}
        setChosenName={setChosenName}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
      <GenderField
        gender={gender}
        options={genderOptions}
        setGender={setGender}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
      <PronounsField
        options={pronounOptions}
        pronouns={pronouns}
        setPronouns={setPronouns}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
    </VStack>
  );
};

export type { BasicInfoSectionProps };
export default BasicInfoSection;
